import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useGetMe } from "@workspace/api-client-react";
import { apiGet, apiPost } from "@/lib/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  RefreshCw, Link2, Unlink, Settings2, CheckCircle2, XCircle, Clock, AlertCircle, Loader2,
} from "lucide-react";

interface Integration {
  id?: string;
  companyId: string;
  platform: string;
  connected: boolean;
  customEndpointUrl?: string | null;
  syncScope?: string | null;
  lastSyncAt?: string | null;
  lastSyncStatus?: string | null;
  tokenExpiry?: string | null;
  updatedAt?: string | null;
}

interface SyncLog {
  id: string;
  integrationId: string;
  companyId: string;
  syncedAt: string;
  recordType: string;
  recordCount: number;
  status: string;
  errorMessage?: string | null;
}

const PLATFORMS: { id: string; name: string; logo: string; description: string }[] = [
  {
    id: "quickbooks",
    name: "QuickBooks Online",
    logo: "QB",
    description: "Sync invoices, bills, payroll journals, and expenses with Intuit QuickBooks Online.",
  },
  {
    id: "xero",
    name: "Xero",
    logo: "XR",
    description: "Push client invoices, supplier bills, and payroll entries to your Xero ledger.",
  },
  {
    id: "zoho",
    name: "Zoho Books",
    logo: "ZB",
    description: "Connect to Zoho Books for automatic invoice, bill, and expense synchronisation.",
  },
];

const PLATFORM_COLORS: Record<string, string> = {
  quickbooks: "#2CA01C",
  xero: "#13B5EA",
  zoho: "#E42527",
};

const SYNC_SCOPE_OPTIONS = [
  { id: "invoices", label: "Client Invoices" },
  { id: "bills", label: "Supplier Bills" },
  { id: "payroll", label: "Payroll Journals" },
  { id: "expenses", label: "Expenses" },
];

function formatDate(date?: string | null) {
  if (!date) return "—";
  return new Date(date).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function StatusBadge({ status }: { status?: string | null }) {
  if (!status) return <span className="text-muted-foreground text-xs">—</span>;
  const isError = status.startsWith("error");
  const isSuccess = status === "success";
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium ${
      isError ? "text-red-600" : isSuccess ? "text-green-600" : "text-amber-600"
    }`}>
      {isError ? <XCircle className="w-3 h-3" /> : isSuccess ? <CheckCircle2 className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
      {isError ? "Error" : isSuccess ? "Success" : status}
    </span>
  );
}

export default function IntegrationsTab({ companyId, isAdmin: isAdminProp }: { companyId: string; isAdmin?: boolean }) {
  const { data: user } = useGetMe();
  const isAdmin = isAdminProp ?? (user?.role === "Admin" || user?.role === "Super Admin");
  const { toast } = useToast();
  const qc = useQueryClient();

  const integrationsKey = ["integrations", companyId];
  const logsKey = ["integration-logs", companyId];
  const cq = `?companyId=${companyId}`;

  const { data: integrations = [], isLoading } = useQuery<Integration[]>({
    queryKey: integrationsKey,
    queryFn: () => apiGet<Integration[]>(`/integrations${cq}`),
    enabled: !!companyId,
  });

  const { data: availability = {} } = useQuery<Record<string, boolean>>({
    queryKey: ["integrations-availability"],
    queryFn: () => apiGet<Record<string, boolean>>(`/integrations/availability`),
    staleTime: 1000 * 60 * 10,
  });

  const { data: logs = [], isLoading: logsLoading } = useQuery<SyncLog[]>({
    queryKey: logsKey,
    queryFn: () => apiGet<SyncLog[]>(`/integrations/logs${cq}`),
    enabled: !!companyId,
  });

  const getIntegration = (platform: string) =>
    integrations.find(i => i.platform === platform);

  const connectMutation = useMutation({
    mutationFn: async (platform: string) => {
      const data = await apiGet<{ url: string }>(`/integrations/connect/${platform}${cq}`);
      return data.url;
    },
    onSuccess: (url) => {
      window.location.href = url;
    },
    onError: (err: Error & { response?: { data?: { error?: string } } }) => {
      toast({
        title: "Connection failed",
        description: err.response?.data?.error ?? err.message ?? "Could not initiate OAuth flow.",
        variant: "destructive",
      });
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: async (platform: string) => {
      if (platform === "custom") {
        return apiPost(`/integrations/custom/disconnect${cq}`, {});
      }
      return apiPost(`/integrations/disconnect/${platform}${cq}`, {});
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: integrationsKey });
      toast({ title: "Disconnected", description: "Integration has been disconnected." });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const syncMutation = useMutation({
    mutationFn: (platform: string) => apiPost(`/integrations/sync/${platform}${cq}`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: integrationsKey });
      qc.invalidateQueries({ queryKey: logsKey });
      toast({ title: "Sync complete", description: "Records synced successfully." });
    },
    onError: (err: Error & { response?: { data?: { error?: string } } }) => {
      qc.invalidateQueries({ queryKey: integrationsKey });
      qc.invalidateQueries({ queryKey: logsKey });
      toast({
        title: "Sync error",
        description: err.response?.data?.error ?? err.message,
        variant: "destructive",
      });
    },
  });

  const [customDialogOpen, setCustomDialogOpen] = useState(false);
  const [customForm, setCustomForm] = useState({ url: "", apiKey: "", syncScope: [] as string[] });

  const customIntegration = getIntegration("custom");

  function openCustomDialog() {
    setCustomForm({
      url: customIntegration?.customEndpointUrl ?? "",
      apiKey: "",
      syncScope: customIntegration?.syncScope?.split(",").filter(Boolean) ?? [],
    });
    setCustomDialogOpen(true);
  }

  const saveCustomMutation = useMutation({
    mutationFn: () =>
      apiPost(`/integrations/custom${cq}`, {
        endpointUrl: customForm.url,
        apiKey: customForm.apiKey || undefined,
        syncScope: customForm.syncScope.join(","),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: integrationsKey });
      setCustomDialogOpen(false);
      toast({ title: "Custom endpoint saved", description: "Your custom endpoint configuration has been saved." });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center text-muted-foreground">
          <AlertCircle className="w-10 h-10 mx-auto mb-3 text-muted-foreground/50" />
          <p className="font-medium">Admin access required</p>
          <p className="text-sm mt-1">Only company admins can manage integrations.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold mb-1">Accounting Integrations</h2>
        <p className="text-muted-foreground text-sm">
          Connect FinProject to your accounting platform to sync invoices, bills, payroll, and expenses automatically.
        </p>
      </div>

      {/* Platform cards */}
      <div className="grid gap-5 sm:grid-cols-1 lg:grid-cols-3">
        {PLATFORMS.map(platform => {
          const integration = getIntegration(platform.id);
          const connected = !!(integration?.connected);
          const syncing = syncMutation.isPending && (syncMutation.variables as string) === platform.id;
          const connecting = connectMutation.isPending && (connectMutation.variables as string) === platform.id;
          const disconnecting = disconnectMutation.isPending && (disconnectMutation.variables as string) === platform.id;
          const isAvailable = availability[platform.id] !== false;

          return (
            <div
              key={platform.id}
              className="bg-card border border-border rounded-2xl p-6 flex flex-col gap-4 shadow-sm"
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center text-white text-sm font-bold shrink-0"
                  style={{ backgroundColor: PLATFORM_COLORS[platform.id] }}
                >
                  {platform.logo}
                </div>
                <div className="min-w-0">
                  <div className="font-semibold text-base leading-tight">{platform.name}</div>
                  <div className="mt-0.5">
                    {connected ? (
                      <Badge className="bg-green-500/10 text-green-700 border-green-200 text-[10px] px-2 py-0">
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Connected
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="text-[10px] px-2 py-0">
                        Not Connected
                      </Badge>
                    )}
                  </div>
                </div>
              </div>

              <p className="text-sm text-muted-foreground leading-relaxed">{platform.description}</p>

              {connected && (
                <div className="text-xs text-muted-foreground space-y-1 border-t border-border pt-3">
                  <div className="flex justify-between">
                    <span>Last sync</span>
                    <span className="font-medium">{formatDate(integration?.lastSyncAt)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>Status</span>
                    <StatusBadge status={integration?.lastSyncStatus} />
                  </div>
                </div>
              )}

              <div className="flex gap-2 mt-auto pt-1">
                {connected ? (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 rounded-xl"
                      disabled={syncing || disconnecting}
                      onClick={() => syncMutation.mutate(platform.id)}
                    >
                      {syncing ? (
                        <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Syncing…</>
                      ) : (
                        <><RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Sync Now</>
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="rounded-xl text-muted-foreground hover:text-destructive"
                      disabled={disconnecting || syncing}
                      onClick={() => disconnectMutation.mutate(platform.id)}
                    >
                      {disconnecting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Unlink className="w-3.5 h-3.5" />}
                    </Button>
                  </>
                ) : isAvailable ? (
                  <Button
                    size="sm"
                    className="flex-1 rounded-xl"
                    disabled={connecting || isLoading}
                    onClick={() => connectMutation.mutate(platform.id)}
                  >
                    {connecting ? (
                      <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Connecting…</>
                    ) : (
                      <><Link2 className="w-3.5 h-3.5 mr-1.5" /> Connect</>
                    )}
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1 rounded-xl cursor-not-allowed opacity-60"
                    disabled
                    title="This integration is not yet configured. Contact the app owner to enable it."
                  >
                    <Clock className="w-3.5 h-3.5 mr-1.5" /> Coming soon
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Custom endpoint */}
      <div className="bg-card border border-border rounded-2xl p-6 shadow-sm">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-slate-700 flex items-center justify-center shrink-0">
              <Settings2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-semibold text-base">Custom / Local Accounting System</div>
              <div className="mt-0.5">
                {customIntegration?.connected ? (
                  <Badge className="bg-green-500/10 text-green-700 border-green-200 text-[10px] px-2 py-0">
                    <CheckCircle2 className="w-3 h-3 mr-1" /> Connected
                  </Badge>
                ) : (
                  <Badge variant="secondary" className="text-[10px] px-2 py-0">Not Configured</Badge>
                )}
              </div>
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button variant="outline" size="sm" className="rounded-xl" onClick={openCustomDialog}>
              <Settings2 className="w-3.5 h-3.5 mr-1.5" />
              {customIntegration?.connected ? "Edit" : "Configure"}
            </Button>
            {customIntegration?.connected && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-xl"
                  disabled={syncMutation.isPending}
                  onClick={() => syncMutation.mutate("custom")}
                >
                  {syncMutation.isPending && (syncMutation.variables as string) === "custom" ? (
                    <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Syncing…</>
                  ) : (
                    <><RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Sync Now</>
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="rounded-xl text-muted-foreground hover:text-destructive"
                  onClick={() => disconnectMutation.mutate("custom")}
                >
                  <Unlink className="w-3.5 h-3.5" />
                </Button>
              </>
            )}
          </div>
        </div>

        {customIntegration?.connected && (
          <div className="mt-4 pt-4 border-t border-border grid grid-cols-2 gap-3 text-sm text-muted-foreground">
            <div>
              <span className="font-medium text-foreground">Endpoint</span>
              <p className="mt-0.5 truncate">{customIntegration.customEndpointUrl ?? "—"}</p>
            </div>
            <div>
              <span className="font-medium text-foreground">Sync scope</span>
              <p className="mt-0.5">{customIntegration.syncScope ? customIntegration.syncScope.split(",").join(", ") : "—"}</p>
            </div>
            <div>
              <span className="font-medium text-foreground">Last sync</span>
              <p className="mt-0.5">{formatDate(customIntegration.lastSyncAt)}</p>
            </div>
            <div>
              <span className="font-medium text-foreground">Status</span>
              <div className="mt-0.5"><StatusBadge status={customIntegration.lastSyncStatus} /></div>
            </div>
          </div>
        )}

        {!customIntegration?.connected && (
          <p className="mt-3 text-sm text-muted-foreground">
            Connect to any local or custom accounting system via a REST endpoint. Enter your API URL, authentication token, and choose which data types to sync.
          </p>
        )}
      </div>

      {/* Sync log table */}
      <div className="bg-card border border-border rounded-2xl shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <h3 className="font-semibold">Recent Sync Activity</h3>
          <Button
            variant="ghost"
            size="sm"
            className="rounded-lg"
            onClick={() => qc.invalidateQueries({ queryKey: logsKey })}
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
        </div>
        {logsLoading ? (
          <div className="flex items-center justify-center h-24 text-muted-foreground text-sm">
            <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading logs…
          </div>
        ) : logs.length === 0 ? (
          <div className="flex items-center justify-center h-24 text-muted-foreground text-sm">
            No sync activity yet. Connect an integration and click "Sync Now" to get started.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>Platform</TableHead>
                  <TableHead>Record Type</TableHead>
                  <TableHead className="text-right">Records</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Error</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map(log => {
                  const integration = integrations.find(i => i.id === log.integrationId);
                  const platformLabel = PLATFORMS.find(p => p.id === integration?.platform)?.name ?? integration?.platform ?? "Custom";
                  return (
                    <TableRow key={log.id}>
                      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                        {formatDate(log.syncedAt)}
                      </TableCell>
                      <TableCell className="text-sm font-medium">{platformLabel}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{log.recordType}</TableCell>
                      <TableCell className="text-right text-sm">{log.recordCount}</TableCell>
                      <TableCell><StatusBadge status={log.status} /></TableCell>
                      <TableCell className="text-xs text-red-500 max-w-[200px] truncate">
                        {log.errorMessage ?? "—"}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Custom endpoint dialog */}
      <Dialog open={customDialogOpen} onOpenChange={setCustomDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Custom Accounting Endpoint</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="custom-url">API Endpoint URL *</Label>
              <Input
                id="custom-url"
                placeholder="https://your-system.example.com/api/v1"
                value={customForm.url}
                onChange={e => setCustomForm(f => ({ ...f, url: e.target.value }))}
                className="rounded-xl"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="custom-key">API Key / Token</Label>
              <Input
                id="custom-key"
                type="password"
                placeholder="Leave blank to keep existing key"
                value={customForm.apiKey}
                onChange={e => setCustomForm(f => ({ ...f, apiKey: e.target.value }))}
                className="rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label>Data to sync</Label>
              <div className="space-y-2">
                {SYNC_SCOPE_OPTIONS.map(opt => (
                  <div key={opt.id} className="flex items-center gap-2">
                    <Checkbox
                      id={`scope-${opt.id}`}
                      checked={customForm.syncScope.includes(opt.id)}
                      onCheckedChange={checked => {
                        setCustomForm(f => ({
                          ...f,
                          syncScope: checked
                            ? [...f.syncScope, opt.id]
                            : f.syncScope.filter(s => s !== opt.id),
                        }));
                      }}
                    />
                    <label htmlFor={`scope-${opt.id}`} className="text-sm cursor-pointer">{opt.label}</label>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCustomDialogOpen(false)}>Cancel</Button>
            <Button
              disabled={!customForm.url || saveCustomMutation.isPending}
              onClick={() => saveCustomMutation.mutate()}
            >
              {saveCustomMutation.isPending ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Saving…</> : "Save Configuration"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
